import { CardPlaceholder } from '@/components/card';
import JSONViewer from '@/components/json-viewer';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { useParticipantSuspiciousActivity } from '@/hooks/useParticipantSuspiciousActivity.hook';
import {
  FormattedWithdrawalDto,
  ParticipantDistributionFeature,
  ParticipantDistributionFeatureUpdateDto
} from '@/models/WithdrawalsDTO.model';
import ParticipantService from '@/services/Participant.service';
import { userService } from '@/services/User.service';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Link as RouterLink } from 'react-router-dom';
import { useToggle } from 'react-use';

import WithdrawalCustomRequestDialog from './WithdrawalCustomRequest/WithdrawalCustomRequestDialog.component';
import { WithdrawalDisbursementDialog } from './WithdrawalCustomRequest/WithdrawalDisbursementDialog.component';

interface WithdrawalRequestsProps {
  participantId: string;
  isVestwellSubaccounting: boolean;
}

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.grey[700],
  fontSize: theme.spacing(2),
  height: theme.spacing(7.8),
  padding: '0',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2)
}));

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'width'
})<{ width?: string }>(props => ({
  fontSize: props.theme.spacing(2),
  fontWeight: props.theme.typography.fontWeightBold,
  width: props.width
}));

const headCells = [
  { id: 'id', label: 'ID' },
  {
    cellWidth: { width: '22%' },
    id: 'status',
    label: 'Status'
  },
  {
    cellWidth: { width: '32%' },
    id: 'withdrawalReason',
    label: 'Type'
  },
  { id: 'requestDate', label: 'Request Date' },
  { cellWidth: { width: '15%' }, id: 'amount', label: 'Amount' }
];

const WithdrawalRequests = (props: WithdrawalRequestsProps): JSX.Element => {
  const { participantId, isVestwellSubaccounting } = props;
  const snackbar = useSnackbar();

  const [isCustomRequestOpen, toggleCustomRequestOpen] = useToggle(false);
  const [isCustomDisbursementOpen, toggleCustomDisbursementOpen] =
    useToggle(false);
  const [showAllowedWithdrawals, toggleAllowedWithdrawals] = useToggle(false);
  const [showWithdrawalSettings, toggleShowWithdrawalSettings] =
    useToggle(false);
  const isTPA = userService.isTpaUser();

  const customReqDialog = (
    <WithdrawalCustomRequestDialog
      isOpen={isCustomRequestOpen}
      onClose={() => toggleCustomRequestOpen(false)}
      participantId={+participantId}
    />
  );

  const WithdrawalsQuery = useQuery<FormattedWithdrawalDto[]>(
    ['ParticipantService.getWithdrawals', participantId],
    () => {
      return ParticipantService.getWithdrawals(+participantId);
    },
    {
      enabled: Boolean(participantId)
    }
  );

  const allowedWithdrawalsQuery = useQuery(
    ['ParticipantService.getAllowedWithdrawals', participantId],
    () => {
      return ParticipantService.getAllowedWithdrawals(+participantId);
    },
    {
      enabled: Boolean(participantId)
    }
  );

  const distributionFeatureQuery = useQuery<ParticipantDistributionFeature>(
    ['ParticipantService.getDistributionFeature', participantId],
    () => {
      return ParticipantService.getDistributionFeature(+participantId);
    },
    {
      enabled: Boolean(participantId)
    }
  );

  const upsertDistributionFeature = useMutation(
    ['ParticipantService.postDistributionFeature'],
    async (data: ParticipantDistributionFeatureUpdateDto) => {
      return await ParticipantService.postDistributionFeature(
        +participantId,
        data
      );
    },
    {
      onSettled(data, error) {
        snackbar.showSnackbar(
          error
            ? {
                message: 'Error updating participant distribution feature!',
                severity: 'error'
              }
            : {
                message: 'Success!',
                severity: 'success'
              }
        );

        if (!error) {
          distributionFeatureQuery.refetch();
        }
      }
    }
  );

  const suspiciousQuery = useParticipantSuspiciousActivity(participantId);
  const suspiciousActivity = suspiciousQuery.data?.hasSuspiciousActivity;

  if (WithdrawalsQuery.isFetching || distributionFeatureQuery.isFetching) {
    return <p>Loading....</p>;
  }

  if (!WithdrawalsQuery.isSuccess) {
    return <p>Error</p>;
  }

  return (
    <>
      <Card variant='outlined'>
        <CardHeader
          action={
            isVestwellSubaccounting &&
            !isTPA && (
              <Stack direction='row'>
                <Button onClick={toggleShowWithdrawalSettings}>Settings</Button>
                <Button onClick={toggleAllowedWithdrawals}>
                  Allowed Withdrawals
                </Button>
                <LoadingButton
                  data-testid='open-custom-request'
                  disabled={
                    WithdrawalsQuery.data.some(i =>
                      ['Processing', 'Pending'].includes(i.status)
                    ) || suspiciousActivity
                  }
                  onClick={() => {
                    toggleCustomRequestOpen(true);
                  }}>
                  CUSTOM WITHDRAWAL REQUEST
                </LoadingButton>
                <Button onClick={toggleCustomDisbursementOpen}>
                  CUSTOM DISBURSEMENT REQUEST
                </Button>

                <WithdrawalDisbursementDialog
                  isOpen={isCustomDisbursementOpen}
                  onClose={toggleCustomDisbursementOpen}
                  participantId={+participantId}
                />
                {customReqDialog}
              </Stack>
            )
          }
          title='Withdrawals'
        />
        {!WithdrawalsQuery.data?.length ? (
          <CardPlaceholder
            data-testid='no-data-withdrawals-participant'
            subtitle='No data for this participant'
          />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map(headCell => (
                    <StyledTableCell
                      key={headCell.id}
                      width={headCell.cellWidth?.width}>
                      {headCell.label}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {WithdrawalsQuery.data?.map(
                  (withdrawal: FormattedWithdrawalDto) => {
                    return (
                      <TableRow key={withdrawal.id}>
                        <StyledTableCellBody>
                          <Link
                            component={RouterLink}
                            to={`/participant/${participantId}/withdrawals/${withdrawal.id}/details`}>
                            {withdrawal.id}
                          </Link>
                        </StyledTableCellBody>

                        <StyledTableCellBody>
                          {withdrawal.status}
                        </StyledTableCellBody>
                        <StyledTableCellBody>
                          {withdrawal.reason}
                        </StyledTableCellBody>
                        <StyledTableCellBody>
                          {withdrawal.dateRequested}
                        </StyledTableCellBody>
                        <StyledTableCellBody>
                          {withdrawal.amount}
                        </StyledTableCellBody>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      <Dialog
        onClose={toggleShowWithdrawalSettings}
        open={showWithdrawalSettings}>
        <DialogTitle>Withdrawal Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    distributionFeatureQuery.data?.data
                      ?.allowPlanResidualDistribution === false
                      ? false
                      : distributionFeatureQuery.data?.data
                          ?.allowParticipantResidualDistribution ?? true
                  }
                  disabled={
                    distributionFeatureQuery.data?.data
                      ?.allowPlanResidualDistribution === false ||
                    distributionFeatureQuery.isFetching ||
                    upsertDistributionFeature.isLoading
                  }
                  onChange={async (
                    event: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    await upsertDistributionFeature.mutateAsync({
                      allowResidualDistribution: event.target.checked
                    });
                  }}
                />
              }
              label='Allow Residual Distribution'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={
                    distributionFeatureQuery.data?.data
                      ?.allowPlanWithdrawalStp === false
                      ? false
                      : distributionFeatureQuery.data?.data
                          ?.allowParticipantWithdrawalStp ?? true
                  }
                  disabled={
                    distributionFeatureQuery.data?.data
                      ?.allowPlanWithdrawalStp === false ||
                    distributionFeatureQuery.isFetching ||
                    upsertDistributionFeature.isLoading
                  }
                  onChange={async (
                    event: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    await upsertDistributionFeature.mutateAsync({
                      allowWithdrawalStp: event.target.checked
                    });
                  }}
                />
              }
              label='Allow Withdrawal STP'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={toggleShowWithdrawalSettings}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth='md'
        onClose={toggleAllowedWithdrawals}
        open={showAllowedWithdrawals}>
        <DialogTitle>Allowed Withdrawals</DialogTitle>
        <DialogContent>
          <JSONViewer json={allowedWithdrawalsQuery.data ?? []} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WithdrawalRequests;
